
import { useState } from 'react';
import { Search, Tag } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { MenuCategory } from '@/types/menu';
import { useTranslation } from 'react-i18next';

interface PublicMenuSearchFiltersProps {
  searchTerm: string;
  setSearchTerm: (term: string) => void;
  selectedCategory: string;
  setSelectedCategory: (category: string) => void;
  selectedTags: string[];
  setSelectedTags: (tags: string[] | ((prev: string[]) => string[])) => void;
  setShowTagsFilter: (show: boolean) => void;
  categories: MenuCategory[];
  onSearchChange?: (term: string) => void; // Added for compatibility
  activeCategory?: string; // Added for compatibility
  onCategoryChange?: (category: string) => void; // Added for compatibility
  onOpenTagsFilter?: () => void; // Added for compatibility
}

export function PublicMenuSearchFilters({
  searchTerm,
  setSearchTerm,
  selectedCategory,
  setSelectedCategory,
  selectedTags,
  setSelectedTags,
  setShowTagsFilter,
  categories,
  onSearchChange, // Added for compatibility
  activeCategory, // Added for compatibility
  onCategoryChange, // Added for compatibility
  onOpenTagsFilter, // Added for compatibility
}: PublicMenuSearchFiltersProps) {
  const { t } = useTranslation();

  // Handle compatibility with both prop patterns
  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchTerm(value);
    if (onSearchChange) onSearchChange(value);
  };

  const handleCategoryChange = (value: string) => {
    setSelectedCategory(value);
    if (onCategoryChange) onCategoryChange(value);
  };

  const handleOpenTagsFilter = () => {
    setShowTagsFilter(true);
    if (onOpenTagsFilter) onOpenTagsFilter();
  };

  const toggleTag = (tag: string) => {
    setSelectedTags((prev: string[]) => {
      if (prev.includes(tag)) {
        return prev.filter((t) => t !== tag);
      } else {
        return [...prev, tag];
      }
    });
  };

  // Use either provided or calculated values
  const activeCat = activeCategory !== undefined ? activeCategory : selectedCategory;
  const tagCount = selectedTags.length;

  return (
    <div className='sticky top-0 z-10 bg-background p-4 border-b shadow-sm mb-6'>
      <div className='flex items-center gap-2 mb-4'>
        <div className='relative flex-1'>
          <Search className='absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground' />
          <Input
            type='search'
            placeholder='Search menu...'
            value={searchTerm}
            onChange={handleSearchChange}
            className='w-full pl-9'
          />
        </div>

        <Button
          variant='outline'
          size='icon'
          className={selectedTags.length > 0 ? 'bg-primary/10' : ''}
          onClick={handleOpenTagsFilter}>
          <Tag className='h-4 w-4' />
          {tagCount > 0 && (
            <span className='absolute -top-2 -right-2 bg-primary text-white rounded-full w-5 h-5 flex items-center justify-center text-xs'>
              {tagCount}
            </span>
          )}
        </Button>
      </div>

      {/* Category Tabs */}
      <Tabs
        value={activeCat}
        onValueChange={handleCategoryChange}
        className='w-full'>
        <TabsList className='w-full mb-2 overflow-x-auto flex-nowrap overflow-y-hidden whitespace-nowrap h-auto py-1 px-1'>
          <TabsTrigger value='all' className='px-4'>
            All Items
          </TabsTrigger>
          {categories.map((category) => (
            <TabsTrigger
              key={category.id}
              value={category.id}
              className='px-4'>
              {category.name}
            </TabsTrigger>
          ))}
        </TabsList>
      </Tabs>

      {/* Selected Tags Display */}
      {selectedTags.length > 0 && (
        <div className='flex flex-wrap gap-1 mt-2'>
          {selectedTags.map((tag) => (
            <Badge
              key={tag}
              variant='secondary'
              className='cursor-pointer'
              onClick={() => toggleTag(tag)}>
              {tag} ✕
            </Badge>
          ))}
          <Button
            variant='ghost'
            size='sm'
            className='text-xs h-6 ml-1'
            onClick={() => setSelectedTags([])}>
            Clear All
          </Button>
        </div>
      )}
    </div>
  );
}
